import React, { FunctionComponent, ReactNode } from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { PrimaryButton, PrimaryButtonProps } from '../../atoms/primaryButton/PrimaryButton'
import { getCommonStyles } from '../../styles/commonStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'

/**
 * Currently used with the sticky view component to have a fixed bottom button on mobile
 */

export interface FixedBottomButtonProps extends PrimaryButtonProps {
  backgroundColor?: string
  isTransparent?: boolean
  secondaryButton?: ReactNode
  children?: ReactNode
  customContainerStyle?: CSSObject
  disabled?: boolean
  stackButtonsVertically?: boolean
}

const Container = styled.View<{
  theme: ThemeType
  bottomInset: number
  backgroundColor: string
  isTransparent: boolean
  customStyle?: CSSObject
  stackButtonsVertically?: boolean
}>(({ theme, bottomInset, backgroundColor, isTransparent, customStyle, stackButtonsVertically }) => {
  return theme.breakpoints.isMobileSized
    ? {
        ...getCommonStyles(theme.colors, theme.fontFamilyMetadata).fixedButtonContainer,
        borderTopWidth: isTransparent ? 0 : '1px',
        width: '100%',
        paddingBottom: bottomInset > 0 ? '12px' : '34px',
        backgroundColor,
        flexDirection: 'column-reverse',
        gap: theme.spacing['16px'],
        ...customStyle,
      }
    : {
        marginVertical: theme.spacing['32px'],
        ...(stackButtonsVertically
          ? {
              width: '100%',
              flexDirection: 'column-reverse',
              justifyContent: 'center',
              alignItems: 'center',
              gap: theme.spacing['16px'],
            }
          : {
              marginLeft: 'auto',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }),
        ...customStyle,
      }
})

// Minimum button width for self-pay extension vertical layout to match design specs
const SELF_PAY_BUTTON_MIN_WIDTH = 343

const SecondaryButtonWrapper = styled.View<{ stackButtonsVertically: boolean }>(
  ({ stackButtonsVertically }) =>
    stackButtonsVertically && {
      minWidth: SELF_PAY_BUTTON_MIN_WIDTH,
    },
)

export const FixedBottomButton: FunctionComponent<FixedBottomButtonProps> = ({
  text,
  onPress,
  loading,
  backgroundColor,
  children,
  isTransparent = false,
  secondaryButton,
  customContainerStyle,
  disabled,
  stackButtonsVertically = false,
  ...rest
}) => {
  const { colors, breakpoints } = useTheme()
  const insets = useSafeAreaInsets()
  const shouldUseFullWidth = breakpoints.isMobileSized || stackButtonsVertically
  return (
    <Container
      bottomInset={insets.bottom}
      backgroundColor={backgroundColor || colors.backgroundPrimary}
      isTransparent={isTransparent}
      customStyle={customContainerStyle}
      stackButtonsVertically={stackButtonsVertically}
    >
      {children}
      {secondaryButton && (
        <SecondaryButtonWrapper stackButtonsVertically={stackButtonsVertically}>
          {secondaryButton}
        </SecondaryButtonWrapper>
      )}
      <PrimaryButton
        text={text}
        onPress={onPress}
        fullWidth={shouldUseFullWidth}
        loading={loading}
        disabled={disabled}
        style={stackButtonsVertically ? { minWidth: SELF_PAY_BUTTON_MIN_WIDTH } : undefined}
        {...rest}
      />
    </Container>
  )
}
